import { computed, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useSettingsStore } from '@/stores/settings'
import { useAuthStore } from '@/sync/auth'
import { getSupabase } from '@/sync/supabase'
import { createSyncedDoc, type SyncedDoc } from '@/sync/yjs-sync'
import { connectCollection } from '@/sync/connect'
import * as repo from '@/db/repositories'
import type { ReviewCard } from '@/types/domain'

/**
 * Composable que sincroniza las tarjetas de repaso vía CRDT (Área E).
 *
 * Espejo de `useShelvesSync`: un ÚNICO doc global por usuario
 * (room `reviews:${userId}`) con un `Y.Array<ReviewCard>`. Las tarjetas son
 * cross-libro y su progreso debe cruzar dispositivos, igual que las estanterías.
 *
 * Reutiliza `connectCollection` (add/remove/update por id) y
 * `repo.reconcileReviewCards` como persistencia local de los cambios remotos.
 */

/** Doc activo a nivel de módulo (singleton global de tarjetas de repaso). */
let current: { synced: SyncedDoc; cleanup: () => void } | null = null

// Flush final al cerrar la pestaña, igual que en useSync.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    current?.synced.flushBeacon()
  })
}

export function useReviewsSync() {
  const settings = useSettingsStore()
  const auth = useAuthStore()
  const { settings: settingsRefs } = storeToRefs(settings)

  const syncEnabled = computed(
    () => settingsRefs.value.sync.enabled && auth.isAuthenticated,
  )

  /**
   * Abre el doc global de tarjetas de repaso y lo conecta al ref del store.
   * Devuelve true si el sync quedó activo; false si no hay sesión/sync.
   * Idempotente: si ya está abierto, no hace nada.
   */
  function openReviewsSync(cardsRef: Ref<ReviewCard[]>): boolean {
    if (!syncEnabled.value || !auth.userId) return false

    const client = getSupabase(settingsRefs.value.sync)
    if (!client) return false

    if (current) return true

    const room = `reviews:${auth.userId}`
    const syncCfg = settingsRefs.value.sync
    const restBase = syncCfg.supabaseUrl ? `${syncCfg.supabaseUrl.replace(/\/$/, '')}/rest/v1` : undefined
    const synced = createSyncedDoc(room, client, auth.userId, restBase, syncCfg.supabaseAnonKey)
    current = { synced, cleanup: () => {} }

    connectCollection(cardsRef, synced, 'reviewCards', repo.reconcileReviewCards)
      .then((result) => {
        if (current) {
          current.cleanup = result.disconnect
        } else {
          result.disconnect()
        }
      })
      .catch((e) => console.warn('[sync-reviews] conexión falló:', e))

    return true
  }

  /** Cierra el doc de tarjetas de repaso (persiste y desconecta). */
  function closeReviewsSync() {
    if (current) {
      current.cleanup()
      current.synced.destroy()
      current = null
    }
  }

  return { syncEnabled, openReviewsSync, closeReviewsSync }
}
